import { useEffect } from "react";
import { SITE_NAME, imgUrl } from "@/lib/api";
import { useSeo } from "@/lib/layout";

const VERIFY = {
  verify_google: "google-site-verification",
  verify_bing: "msvalidate.01",
  verify_yandex: "yandex-verification",
  verify_pinterest: "p:domain_verify",
  verify_facebook: "facebook-domain-verification",
};

export const absUrl = (u) => (!u ? undefined : u.startsWith("http") ? u : u.startsWith("/api/") ? imgUrl(u) : window.location.origin + u);

const upsert = (selector, tag, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

const setMeta = (attr, key, content) => {
  const selector = `meta[${attr}="${key}"]`;
  if (!content) return document.head.querySelector(selector)?.remove();
  upsert(selector, "meta", { [attr]: key, content: String(content) });
};

export const Seo = ({ title, description, image, type = "website", jsonLd, noindex, article, canonical, social, raw }) => {
  const { seo, site } = useSeo();
  const fullTitle = raw ? title : (seo.title_template || "{titulo} | {sitio}").replace("{titulo}", title).replace("{sitio}", site);
  const key = JSON.stringify({ fullTitle, description, image, type, jsonLd, noindex, article, canonical, social, seo });
  useEffect(() => {
    const url = canonical || window.location.origin + window.location.pathname;
    const ogImage = social?.image || image || absUrl(seo.default_share_image) || window.location.origin + "/logo.png";
    const robots = noindex || seo.noindex_site
      ? "noindex, nofollow"
      : `index, follow, max-image-preview:${seo.max_image_preview || "large"}, max-snippet:${seo.max_snippet ?? -1}, max-video-preview:-1`;
    document.title = fullTitle;
    setMeta("name", "description", description);
    setMeta("name", "robots", robots);
    const og = { "og:title": social?.title || fullTitle, "og:description": social?.description || description, "og:type": type, "og:url": url, "og:image": ogImage, "og:site_name": site, "og:locale": "es_ES" };
    Object.entries(og).forEach(([k, v]) => setMeta("property", k, v));
    const tw = { "twitter:card": "summary_large_image", "twitter:title": social?.title || fullTitle, "twitter:description": social?.description || description, "twitter:image": ogImage, "twitter:site": seo.twitter_handle };
    Object.entries(tw).forEach(([k, v]) => setMeta("name", k, v));
    ["published_time", "modified_time", "section"].forEach((k) => setMeta("property", `article:${k}`, article?.[k]));
    upsert('link[rel="canonical"]', "link", { rel: "canonical", href: url });
    const ld = upsert('script[data-seo="ld"]', "script", { type: "application/ld+json", "data-seo": "ld" });
    ld.textContent = JSON.stringify(jsonLd || []);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

export const SiteHead = () => {
  const { seo } = useSeo();
  useEffect(() => {
    Object.entries(VERIFY).forEach(([k, name]) => setMeta("name", name, seo[k]));
  }, [seo]);
  return null;
};

export const orgLd = (seo = {}, site = SITE_NAME) => {
  const origin = window.location.origin;
  const org = {
    "@context": "https://schema.org",
    "@type": "NewsMediaOrganization",
    name: seo.org_name || site,
    url: origin + "/",
    logo: { "@type": "ImageObject", url: absUrl(seo.org_logo) || origin + "/logo.png" },
    publishingPrinciples: absUrl(seo.publishing_principles_url || "/sobre-nosotros"),
    areaServed: "ES",
    inLanguage: "es-ES",
  };
  const optional = { legalName: seo.org_legal_name, foundingDate: seo.org_founding_year, email: seo.org_email, telephone: seo.org_phone, sameAs: seo.same_as?.length ? seo.same_as : undefined };
  Object.entries(optional).forEach(([k, v]) => { if (v) org[k] = v; });
  if (seo.org_city || seo.org_country) org.address = { "@type": "PostalAddress", addressLocality: seo.org_city || undefined, addressCountry: seo.org_country || undefined };
  return org;
};

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: window.location.origin + it.path })),
});
